import { CircleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, Outlet, useOutletContext, useParams } from 'react-router';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { useAdministrationScopes } from './AdministrationScopesProvider';
import { sectionPath, type Place, type PlaceDetail, type SectionId } from './place';
import { canAt, landingPlace, placeEntryPath, sectionsFor } from './scopes';
import { usePlace } from './use-place';

export type PlaceContext = {
  readonly place: Place;
  readonly detail: PlaceDetail;
  readonly sections: readonly SectionId[];
  /** Reads the place again: after a refusal that says the caller's standing changed. */
  readonly refreshPlace: () => void;
};

/**
 * One place of the area: `/admin/tenants/:placeId/…` or `/admin/organizations/:placeId/…`. It reads
 * the place, then hands its sections the place and what the caller may do there.
 *
 * The three failures are said as what they are (FR-UX-4): a place the caller does not administer
 * (one answer for missing, malformed and not administered, as the server gives it), a read that
 * did not answer, and — for a section — a section this place does not have for this caller.
 */
export function PlaceLayout({ kind }: { kind: 'tenant' | 'organization' }) {
  const { placeId = '' } = useParams();
  const place: Place = { kind, id: placeId };
  return <PlaceFrame place={place} />;
}

/** The platform place, which needs no read: the global grant's own entry stands for it. */
export function PlatformLayout() {
  return <PlaceFrame place={{ kind: 'platform' }} />;
}

function PlaceFrame({ place }: { place: Place }) {
  const { t } = useTranslation('administration');
  const { state, retry } = usePlace(place);
  const { forgetPlace, reload } = useAdministrationScopes();

  if (state.status === 'loading') {
    return (
      <SkeletonRegion label={t('place.loading')} className="flex flex-col gap-3 p-1">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-4 w-80" />
        <Skeleton className="mt-4 h-40 w-full" />
      </SkeletonRegion>
    );
  }

  if (state.status === 'not-administered') {
    return (
      <div role="alert" className="flex flex-col items-start gap-3 p-1">
        <p className="text-sm">{t('place.notAdministered')}</p>
        <Button asChild variant="outline">
          <Link to="/admin">{t('place.chooseAnother')}</Link>
        </Button>
      </div>
    );
  }

  if (state.status === 'unavailable') {
    return (
      <div role="alert" className="flex flex-col items-start gap-3 p-1">
        <p className="flex items-center gap-2 text-sm">
          <CircleAlert aria-hidden="true" className="text-destructive size-4" />
          {t('place.unavailable')}
        </p>
        <Button variant="outline" onClick={retry}>
          {t('area.retry')}
        </Button>
      </div>
    );
  }

  const context: PlaceContext = {
    place,
    detail: state.detail,
    sections: sectionsFor(place, state.detail.can),
    // The platform has no place read: its standing comes from the scopes themselves.
    refreshPlace: place.kind === 'platform' ? reload : () => forgetPlace(place),
  };
  // `.admin-place` draws no box (`display: contents`): it only scopes the area's phone sizes in
  // shell.css, so that finance and the clinical screens keep their own.
  return (
    <div className="admin-place">
      <Outlet context={context} />
    </div>
  );
}

export function usePlaceContext(): PlaceContext {
  return useOutletContext<PlaceContext>();
}

/** `/admin/<place>` without a section: the first section this caller has here. */
export function PlaceIndex() {
  const { t } = useTranslation('administration');
  const { place, sections } = usePlaceContext();
  const first = sections[0];
  if (first === undefined) {
    return <p className="text-muted-foreground p-1 text-sm">{t('place.noSections')}</p>;
  }
  return <Navigate to={sectionPath(place, first)} replace />;
}

/** Renders a section only where the place has it for this caller; otherwise says so. */
export function PlaceSection({ id, children }: { id: SectionId; children: ReactNode }) {
  const { t } = useTranslation('administration');
  const { place, sections } = usePlaceContext();
  if (!sections.includes(id)) {
    // Straight to a section this caller has here: a tenant's own address is still today's
    // operator page (FR-AD-12), which a tenant administrator may not open.
    const first = sections[0];
    return (
      <div className="flex flex-col items-start gap-3 p-1">
        <p className="text-sm">{t('place.sectionNotHere')}</p>
        <Button asChild variant="outline">
          {first === undefined ? (
            <Link to="/admin">{t('place.chooseAnother')}</Link>
          ) : (
            <Link to={sectionPath(place, first)}>{t('place.toUsableSection')}</Link>
          )}
        </Button>
      </div>
    );
  }
  return children;
}

/**
 * `/admin`: where the area opens (FR-AD-1) — the shell's active organization when the caller
 * administers it, otherwise their broadest place.
 */
export function AdministrationLanding() {
  const { scopes } = useAdministrationScopes();
  const active = useActiveContextOptional()?.active ?? null;
  const place = landingPlace(scopes, active);
  if (place === null) {
    return null;
  }
  return <Navigate to={placeEntryPath(place, canAt(place, scopes, active?.tenantId))} replace />;
}
